import { useState } from "react";
import { toast } from "sonner";
import { CalendarClock, RefreshCw, Mail, Plus, Power, RotateCcw, Brush, Instagram, Copy } from "lucide-react";
import { api, API, errMsg } from "@/lib/api";
import { BuildButton, STEP2 } from "@/components/reloc/RelocTop";

const day = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }) : "—");
const stamp = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const noteText = (n) => (typeof n === "string" ? n : n?.text || "");

const EditionNotes = ({ g, setG }) => {
  const [text, setText] = useState("");
  const cur = g.edition_history[g.edition_history.length - 1];
  const add = async () => {
    try {
      const { data } = await api.post(`/relocation/guides/${g.id}/editions/${cur.edition}/notes`, { text });
      setG({ ...g, edition_history: data }); setText(""); toast.success("Note added");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="p-8" data-testid="reloc-edition-history">
      <p className="eyebrow mb-5">Edition history</p>
      {[...g.edition_history].reverse().map((e) => (
        <div key={e.edition} className="border-l-2 border-[#C59B27] pl-5 mb-6">
          <p className="font-display text-xl">Edition {e.edition} <span className="font-sans text-xs text-[#8C827A]">{day(e.date)} · {(e.notes || []).length} notes</span></p>
          <p className="italic text-[#3B1B48] mt-1">{e.summary}</p>
          {(e.notes || []).map((n, i) => <p key={i} className="text-sm text-[#8C827A] mt-1">— {noteText(n)}</p>)}
          {e.edition === cur?.edition && (
            <div className="flex gap-2 mt-4">
              <input data-testid="reloc-note-input" value={text} onChange={(ev) => setText(ev.target.value)} onKeyDown={(ev) => ev.key === "Enter" && text.trim() && add()} placeholder="Add a note"
                className="flex-1 border border-[#E2D9CC] bg-white px-3 py-2 text-sm outline-none focus:border-[#3B1B48]" />
              <button data-testid="reloc-note-add" onClick={add} disabled={!text.trim()} className="text-sm text-[#3B1B48] px-3 disabled:text-[#C9BFB3] inline-flex items-center gap-1"><Plus className="w-4 h-4" />Add</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export const RelocAccuracy = ({ g, setG, onStarted }) => {
  const due = g.next_update_due && new Date(g.next_update_due) <= new Date();
  return (
    <div className="grid lg:grid-cols-2 border border-[#E2D9CC]" data-testid="reloc-accuracy">
      <div className="bg-[#F4EFE6] p-8 border-b lg:border-b-0 lg:border-r border-[#E2D9CC]">
        <div className="flex items-center justify-between mb-6">
          <p className="eyebrow flex items-center gap-2"><CalendarClock className="w-4 h-4" />Accuracy · 6-month update cycle</p>
          {g.edition > 0 && <span data-testid="reloc-update-state" className={`font-mono-accent text-[10px] uppercase tracking-wider px-2.5 py-1 border ${due ? "text-[#B45309] border-[#B45309]/40" : "text-[#2D6A4F] border-[#2D6A4F]/40 bg-[#E3EEE7]"}`}>{due ? "Update due" : "Up to date"}</span>}
        </div>
        <div className="grid grid-cols-3 gap-4 mb-7 text-sm">
          <div><p className="text-xs text-[#8C827A]">Edition</p><p className="font-display text-2xl mt-1">{g.edition}</p></div>
          <div><p className="text-xs text-[#8C827A]">Last updated</p><p className="mt-1">{day(g.last_updated_at)}</p></div>
          <div><p className="text-xs text-[#8C827A]">Next update due</p><p className="mt-1">{day(g.next_update_due)}</p></div>
        </div>
        <BuildButton g={g} mode="update" icon={RefreshCw} label="Update guide now" testId="reloc-update-now" onStarted={onStarted} className="inline-flex items-center gap-2 bg-[#3B1B48] text-[#FAF7F2] px-5 py-3 text-sm hover:bg-[#4A1E5C] disabled:opacity-50 disabled:cursor-not-allowed" />
        <p className="text-xs text-[#8C827A] mt-4 max-w-md leading-relaxed">Re-researches every source, rewrites all chapters, re-typesets the PDFs and toolkit and logs what changed. Your store listing and publish status are kept, and the edition number goes up by one.</p>
        <div className="border-t border-[#E2D9CC] mt-6 pt-4 flex flex-wrap items-center gap-4 text-xs text-[#8C827A]">
          <span className="flex items-center gap-2 mr-auto"><Mail className="w-4 h-4" />Email reminders: 2 weeks before and on the due date</span>
          <span title={STEP2} className="text-[#C9BFB3]">Send test reminder</span>
          <span title={STEP2} className="text-[#C9BFB3]">Email past buyers ({g.buyers?.count || 0})</span>
        </div>
      </div>
      <EditionNotes g={g} setG={setG} />
    </div>
  );
};

export const RelocRetries = ({ g }) => {
  const items = [...g.retry_history].reverse();
  const resumes = g.retry_history.filter((r) => r.kind === "resume").length;
  return (
    <div className="border border-[#E2D9CC] bg-[#FAF7F2] p-7" data-testid="reloc-retries">
      <div className="flex flex-wrap justify-between gap-2 mb-4">
        <p className="font-mono-accent text-xs tracking-[0.15em] uppercase text-[#3B1B48]">Retry history</p>
        <p className="text-sm text-[#8C827A]">{g.retry_history.length - resumes} automatic retries · {resumes} resumes after server restart</p>
      </div>
      {items.length === 0 && <p className="text-sm text-[#8C827A]">This guide built without any retries.</p>}
      <div className="max-h-72 overflow-auto">
        {items.map((r, i) => (
          <div key={i} className="flex gap-3 py-3 border-b border-[#E2D9CC] last:border-0">
            {r.kind === "resume" ? <Power className="w-4 h-4 text-[#8C827A] mt-0.5" /> : <RotateCcw className="w-4 h-4 text-[#8C827A] mt-0.5" />}
            <div className="text-sm"><p><span className="font-medium capitalize">{r.kind} {r.attempt}</span> <span className="text-[#8C827A]">· {stamp(r.ts)}</span></p><p className="text-[#1C181D]/80">{r.reason}</p></div>
          </div>
        ))}
      </div>
    </div>
  );
};

const img = (g, key) => `${API}/relocation/guides/${g.id}/files/${key}?inline=true`;

export const RelocIllustrations = ({ g, onStarted }) => (
  <div className="grid md:grid-cols-[320px_1fr] border border-[#E2D9CC] bg-[#FAF7F2]" data-testid="reloc-illustrations">
    <div className="bg-[#F4EFE6] min-h-[260px]">{g.has_illus_cover && <img src={img(g, "illus_cover")} alt="Cover illustration" className="w-full h-full object-cover" />}</div>
    <div className="p-8 min-w-0">
      <p className="eyebrow mb-3">Illustrations</p>
      <p className="mb-5">{g.illustrations.length + (g.has_illus_cover ? 1 : 0)} gold line illustrations: the cover plus one for each chapter opener.</p>
      <BuildButton g={g} mode="redraw" icon={Brush} label="Redraw illustrations" testId="reloc-redraw" onStarted={onStarted} />
      <div className="flex gap-3 overflow-x-auto mt-6 pb-2">
        {g.illustrations.map((k) => (
          <a key={k} href={img(g, k)} target="_blank" rel="noreferrer" className="shrink-0" data-testid={`reloc-illus-${k.slice(6)}`}>
            <img src={img(g, k)} alt={`Chapter ${k.slice(6)}`} loading="lazy" className="w-28 h-20 object-cover border border-[#E2D9CC]" />
            <p className="font-mono-accent text-[10px] text-[#8C827A] mt-1">Ch {k.slice(6)}</p>
          </a>
        ))}
      </div>
    </div>
  </div>
);

export const RelocInstagramPost = ({ g }) => {
  const ig = g.instagram || {};
  const tags = (ig.hashtags || []).map((h) => (h.startsWith("#") ? h : `#${h}`)).join(" ");
  const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copied"), () => toast.error("Could not copy — select the text and copy it manually"));
  const slides = [1, 2, 3, 4, 5].filter((n) => g.files[`carousel_${n}`]);
  return (
    <div className="grid md:grid-cols-[320px_1fr] border border-[#E2D9CC] bg-[#FAF7F2]" data-testid="reloc-instagram">
      <div className="bg-[#3B1B48] grid place-items-center p-4">{g.files.instagram_image && <img src={img(g, "instagram_image")} alt="Instagram post" className="w-full aspect-square object-cover" />}</div>
      <div className="p-8 min-w-0 space-y-5">
        <div className="flex justify-between items-center">
          <p className="eyebrow flex items-center gap-2"><Instagram className="w-4 h-4" />Instagram post</p>
          <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-[#8C827A]">New guide · {(ig.hashtags || []).length} hashtags</p>
        </div>
        <textarea readOnly data-testid="reloc-caption" value={ig.caption || ""} rows={7} className="w-full border border-[#E2D9CC] bg-white p-4 text-sm leading-relaxed outline-none" />
        <p className="text-xs text-[#3B1B48] break-words" data-testid="reloc-hashtags">{tags}</p>
        <div className="flex flex-wrap gap-2">
          <button data-testid="reloc-copy-caption" onClick={() => copy(`${ig.caption || ""}\n\n${tags}`)} className="inline-flex items-center gap-2 border border-[#E2D9CC] px-4 py-2 text-sm hover:border-[#3B1B48]"><Copy className="w-4 h-4" />Copy caption + hashtags</button>
          {ig.first_comment && <button data-testid="reloc-copy-comment" onClick={() => copy(ig.first_comment)} className="inline-flex items-center gap-2 border border-[#E2D9CC] px-4 py-2 text-sm hover:border-[#3B1B48]"><Copy className="w-4 h-4" />Copy first comment</button>}
        </div>
        {slides.length > 0 && (
          <div className="flex gap-3 overflow-x-auto pb-1">
            {slides.map((n) => <a key={n} href={img(g, `carousel_${n}`)} target="_blank" rel="noreferrer" className="shrink-0"><img src={img(g, `carousel_${n}`)} alt={`Slide ${n}`} loading="lazy" data-testid={`reloc-slide-${n}`} className="w-24 h-24 object-cover border border-[#E2D9CC]" /></a>)}
          </div>
        )}
      </div>
    </div>
  );
};
